import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { BodyCondition, EngineCondition, QuoteType } from "../models/Quote";
import { DefaultResponseWithData } from "../models/system";

export interface ISimulatedCustomerPrice {
  customerId: string;
  competes: boolean;
  notCompetingReason: string | null;
  priced: boolean;
  noPriceReason: string | null;
  priceCollected: number;
  collectedType: QuoteType;
  priceDropOff: number;
  dropOffType: QuoteType;
  calculationJson: string;
}

export interface IQuotePriceSimulation {
  quoteId: string;
  postcode: string;
  vehicleRegistration: string;
  inputs: { engineCondition: EngineCondition; bodyCondition: BodyCondition; alloyWheels: boolean; mileage: number };
  isMarketplaceQuote: boolean;
  storedCollectedCustomerId: string | null;
  storedPriceCollected: number;
  storedCollectedType: QuoteType;
  storedDropOffCustomerId: string | null;
  storedPriceDropOff: number;
  storedDropOffType: QuoteType;
  simulatedCollectedCustomerId: string | null;
  simulatedDropOffCustomerId: string | null;
  customers: ISimulatedCustomerPrice[];
}

/** SAV only. Only fetches while `enabled`, and never caches - the point is to see settings as they are now. */
export default function useQuotePriceSimulation(quoteId: string, enabled: boolean) {
  const { get } = useApiHelper();

  const simulation = useQuery({
    queryKey: ["quote", quoteId, "simulate-prices"],
    queryFn: () => get<DefaultResponseWithData<IQuotePriceSimulation>>(`/quote/${quoteId}/simulate-prices`),
    enabled: enabled && !!quoteId,
    staleTime: 0,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return { simulation };
}
